export type SharePlatform = 'bluesky' | 'linkedin' | 'twitter';

/** Who is doing the sharing. Editorial speaks about the paper, authors speak for it. */
export type ShareVariant = 'published_paper' | 'author_portal';

export type ShareAuthor = {
  first_name?: string | null;
  last_name?: string | null;
};

export type ShareTextParams = {
  journalName: string;
  title: string;
  authors?: readonly ShareAuthor[] | null;
  paperUrl: string;
  /** The approved phrase completing the variant's summary starter. */
  summary?: string | null;
  /** Defaults to the published paper page wording. */
  variant?: ShareVariant;
};

const OPEN_ACCESS_HASHTAG = '#AcademiaOpenAccess';

/** The generator stores only the completing phrase, so each variant supplies its opening. */
export const SHARE_SUMMARY_STARTERS: Record<ShareVariant, string> = {
  published_paper: 'This study explores',
  author_portal: 'In this study we explore',
};

const ELLIPSIS = '…';

type VariantCopy = {
  opening: (journalName: string) => string;
  summaryStarter: string;
  /** The portal speaks on the authors' behalf, so it does not list them. */
  listsAuthors: boolean;
};

const VARIANTS: Record<ShareVariant, VariantCopy> = {
  published_paper: {
    opening: (journalName) => `Just published in ${journalName}:`,
    summaryStarter: SHARE_SUMMARY_STARTERS.published_paper,
    listsAuthors: true,
  },
  author_portal: {
    opening: (journalName) =>
      `Excited to share our paper published in ${journalName}!`,
    summaryStarter: SHARE_SUMMARY_STARTERS.author_portal,
    listsAuthors: false,
  },
};

type PlatformLimit = {
  /** Maximum post length, or null where the platform is effectively unbounded. */
  max: number | null;
  /** Characters a link is billed at, or null to count its real length. */
  linkWeight: number | null;
};

const PLATFORM_LIMITS: Record<SharePlatform, PlatformLimit> = {
  // LinkedIn allows 3000 characters, which the template cannot realistically reach.
  linkedin: { max: null, linkWeight: null },
  bluesky: { max: 300, linkWeight: null },
  // X bills every link at a flat 23 characters, whatever its real length.
  twitter: { max: 280, linkWeight: 23 },
};

const authorName = (author: ShareAuthor): string =>
  [author.first_name, author.last_name]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');

// Submissions can carry a blank trailing author row, which used to render as "null".
export const authorNames = (
  authors?: readonly ShareAuthor[] | null,
): string[] => (authors ?? []).map(authorName).filter(Boolean);

export const journalHashtag = (journalName: string): string =>
  `#${journalName
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')}`;

export const journalHandle = (journalName: string): string =>
  `@${journalName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

// Only LinkedIn has journal profiles today, so nowhere else gets an @mention yet.
const tagLine = (journalName: string, platform: SharePlatform): string =>
  [
    OPEN_ACCESS_HASHTAG,
    journalHashtag(journalName),
    ...(platform === 'linkedin' ? [journalHandle(journalName)] : []),
  ].join(' ');

// Guards against "et al.." and doubled stops after initials.
const endWithStop = (text: string): string =>
  text.endsWith('.') ? text : `${text}.`;

const compose = ({
  copy,
  journalName,
  title,
  authorList,
  summary,
  paperUrl,
  tags,
}: {
  copy: VariantCopy;
  journalName: string;
  title: string;
  authorList: string;
  summary: string;
  paperUrl: string;
  tags: string;
}): string =>
  [
    copy.opening(journalName),
    authorList ? endWithStop(`${title} by ${authorList}`) : title,
    ...(summary ? [endWithStop(`${copy.summaryStarter} ${summary}`)] : []),
    `Read the full article: ${paperUrl}`,
    tags,
  ].join('\n\n');

const postLength = (
  text: string,
  paperUrl: string,
  { linkWeight }: PlatformLimit,
): number =>
  linkWeight === null
    ? text.length
    : text.length - paperUrl.length + linkWeight;

const truncateAtWord = (text: string, maxLength: number): string => {
  const clipped = text.slice(0, Math.max(0, maxLength));
  const lastSpace = clipped.lastIndexOf(' ');
  const trimmed = (
    lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped
  ).trimEnd();

  return trimmed ? `${trimmed}${ELLIPSIS}` : '';
};

// Shed the summary, then the author list, then the title, so link and tags always survive.
const condenseToLimit = (
  params: ShareTextParams,
  copy: VariantCopy,
  tags: string,
  limit: PlatformLimit,
): string => {
  const { journalName, title, paperUrl } = params;
  const names = copy.listsAuthors ? authorNames(params.authors) : [];
  const max = limit.max as number;
  const fits = (text: string) => postLength(text, paperUrl, limit) <= max;
  const base = { copy, journalName, title, paperUrl, tags };

  const full = compose({
    ...base,
    authorList: names.join(', '),
    summary: params.summary ?? '',
  });
  if (fits(full)) return full;

  const withoutSummary = compose({
    ...base,
    authorList: names.join(', '),
    summary: '',
  });
  if (fits(withoutSummary)) return withoutSummary;

  const authorList = names.length > 1 ? `${names[0]} et al.` : names.join('');
  const shortened = compose({ ...base, authorList, summary: '' });
  const overflow = postLength(shortened, paperUrl, limit) - max;
  if (overflow <= 0) return shortened;

  const truncatedTitle = truncateAtWord(
    title,
    title.length - overflow - ELLIPSIS.length,
  );
  if (!truncatedTitle) return shortened;

  return compose({
    ...base,
    title: truncatedTitle,
    authorList,
    summary: '',
  });
};

export const buildShareText = (
  params: ShareTextParams,
  platform: SharePlatform,
): string => {
  const copy = VARIANTS[params.variant ?? 'published_paper'];
  const tags = tagLine(params.journalName, platform);
  const limit = PLATFORM_LIMITS[platform];

  if (limit.max !== null) return condenseToLimit(params, copy, tags, limit);

  return compose({
    copy,
    journalName: params.journalName,
    title: params.title,
    authorList: copy.listsAuthors ? authorNames(params.authors).join(', ') : '',
    summary: params.summary ?? '',
    paperUrl: params.paperUrl,
    tags,
  });
};
